/**
 * The strategic objectives a cycle's tracks may take as criteria (migration 1066).
 *
 * When the cycle supports a strategy (strategyIdForCycle: the cycle's own reference,
 * else the strategy that lists the cycle), its objectives; otherwise every objective
 * in the workspace. Achieved, deferred and cancelled objectives are left out unless a
 * track already has one, so a chosen criterion never disappears from the list.
 *
 * Nothing is fetched while the workspace has Strategy off: `strategyOn` is false and
 * the caller draws nothing.
 */
import { useEffect, useMemo, useState } from 'react';
import { listObjectives, type StrategicObjective } from '@/lib/strategyAlignment';
import { strategyIdForCycle } from '@/lib/strategy/cycleStrategy';
import { objectiveCriterionKey } from '@/lib/objectiveCriteria';
import { isModuleEnabled, useWorkspaceModules, useWorkspaceModulesReady } from '@/lib/workspaceModulesService';
import { useStrategyStore } from '@/stores/prioritization/strategyStore';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';

export interface CycleObjectiveOption {
  objective: StrategicObjective;
  /** The criterion key this objective takes on a track. */
  key: string;
}

export interface CycleObjectives {
  /** False while the workspace has Strategy off (or before modules resolve): draw nothing. */
  strategyOn: boolean;
  loading: boolean;
  options: CycleObjectiveOption[];
  /** The strategy the list comes from, when the cycle supports one. */
  strategyName: string | null;
}

export function useCycleObjectives(cycleId: string | null | undefined, chosenKeys: readonly string[] = []): CycleObjectives {
  const features = useWorkspaceModules();
  const modulesReady = useWorkspaceModulesReady();
  const strategyOn = modulesReady && isModuleEnabled(features, 'strategy');
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const cycle = useDegapProcessStore((s) => (cycleId ? s.cycles.find((c) => c.id === cycleId) : undefined));
  const strategies = useStrategyStore((s) => s.strategies);
  const fetchStrategies = useStrategyStore((s) => s.fetchFromSupabase);

  useEffect(() => {
    if (strategyOn) void fetchStrategies();
  }, [strategyOn, fetchStrategies]);

  const strategyId = useMemo(
    () => (cycleId ? strategyIdForCycle(cycleId, cycle?.strategyRef, Object.values(strategies)) : null),
    [cycleId, cycle?.strategyRef, strategies],
  );

  const [objectives, setObjectives] = useState<StrategicObjective[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!strategyOn || !workspaceId) {
      setObjectives([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void listObjectives(workspaceId, strategyId ?? undefined)
      .then((list) => { if (!cancelled) setObjectives(list); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [strategyOn, workspaceId, strategyId]);

  const chosenKey = chosenKeys.join('|');
  const options = useMemo(() => {
    const chosen = new Set(chosenKey ? chosenKey.split('|') : []);
    return objectives
      .map((objective) => ({ objective, key: objectiveCriterionKey(objective.id) }))
      .filter((o) => o.objective.status === 'active' || chosen.has(o.key));
  }, [objectives, chosenKey]);

  return {
    strategyOn,
    loading,
    options,
    strategyName: strategyId ? strategies[strategyId]?.name ?? null : null,
  };
}
